/**
 * Where the app is showing you events — the one source (top-header handoff, Oct 14 2026).
 *
 * The rule the whole thing is built on: **it never changes by itself.** Not from geolocation,
 * not from an IP guess, not on login, not on an account switch, not on resume. Set Penticton
 * while standing in Kaleden and it stays Penticton until you pick something else. The only
 * things that move it are an explicit pick in the location panel — a city row, a recent, Enter
 * in the search field — or tapping USE MY CURRENT LOCATION, which asks the device once.
 *
 * Keys (the handoff's): `pr_location` is the city name, `pr_location_recent` is
 * `[{ name, at }]`. `pr.userLocation` (`{ lat, lng, label }`) is the older key the wall and
 * search already read, and it is kept in step so every existing consumer follows along.
 * Coordinates per city are cached in `pr_location_coords` — the panel resolves them from
 * Mapbox when a city is picked by name, and geolocation supplies them exactly when used.
 *
 * Changes are broadcast on `window` as `pr-location` and picked up from other tabs through
 * `storage`, so every open surface (header pill, FILTERS tab, Cities panel, search heading,
 * Featured, the wall) shows the same town at the same moment.
 */
const CITY_KEY = 'pr_location';
const RECENT_KEY = 'pr_location_recent';
const COORDS_KEY = 'pr_location_coords';
/** What the wall, search and the City Portal already read. Kept in step, never the master. */
export const LEGACY_KEY = 'pr.userLocation';
export const EVENT = 'pr-location';
/** The town a brand-new account starts on when their profile says nothing. */
export const DEFAULT_CITY = 'Penticton';
const RECENT_MAX = 8;

const read = (k, fallback = null) => {
  try {
    const raw = localStorage.getItem(k);
    return raw == null ? fallback : JSON.parse(raw);
  } catch { return fallback; }
};
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } };

/** The city name only — what the pill and the tab print. */
export function getCity() {
  try {
    const saved = localStorage.getItem(CITY_KEY);
    if (saved) return saved;
  } catch { /* private mode */ }
  const legacy = read(LEGACY_KEY);
  return legacy?.label || null;
}

const coordsMap = () => read(COORDS_KEY, {}) || {};
export const coordsFor = (city) => (city ? coordsMap()[city.trim().toLowerCase()] || null : null);

/** `{ city, lat, lng }` — `lat`/`lng` are null until the city's coordinates are known. */
export function getLocation() {
  const city = getCity();
  if (!city) return { city: null, lat: null, lng: null };
  const legacy = read(LEGACY_KEY);
  const c = coordsFor(city) || (legacy?.label === city ? { lat: legacy.lat, lng: legacy.lng } : null);
  return { city, lat: c?.lat ?? null, lng: c?.lng ?? null };
}

export function getRecents() {
  const list = read(RECENT_KEY, []);
  return Array.isArray(list) ? list.filter((r) => r && typeof r.name === 'string') : [];
}

function broadcast() {
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: getLocation() })); } catch { /* no window */ }
}

/** Remember a city's coordinates without moving the user there (the panel resolves as it lists). */
export function rememberCoords(city, lat, lng) {
  if (!city || typeof lat !== 'number' || typeof lng !== 'number') return;
  const map = coordsMap();
  map[city.trim().toLowerCase()] = { lat, lng };
  write(COORDS_KEY, map);
  // The legacy key carries the coordinates of the CURRENT city, so fill it in once we learn them.
  const current = getCity();
  if (current && current.trim().toLowerCase() === city.trim().toLowerCase()) {
    write(LEGACY_KEY, { lat, lng, label: current });
    broadcast();
  }
}

/**
 * Move the app to a city. This is the only way the location changes, and every caller is an
 * explicit user action. `coords` is optional — the city still works without them, it just
 * cannot narrow a location-aware fetch until they are resolved.
 */
export function setCity(city, coords = null) {
  const name = (city || '').trim();
  if (!name) return getLocation();
  try { localStorage.setItem(CITY_KEY, name); } catch { /* private mode */ }
  if (coords && typeof coords.lat === 'number') rememberCoords(name, coords.lat, coords.lng);
  const c = coords || coordsFor(name);
  write(LEGACY_KEY, { lat: c?.lat ?? null, lng: c?.lng ?? null, label: name });
  const rest = getRecents().filter((r) => r.name.toLowerCase() !== name.toLowerCase());
  write(RECENT_KEY, [{ name, at: Date.now() }, ...rest].slice(0, RECENT_MAX));
  broadcast();
  return getLocation();
}

/** CLEAR keeps the city you are on and forgets the rest. */
export function clearRecents() {
  const city = getCity();
  write(RECENT_KEY, city ? [{ name: city, at: Date.now() }] : []);
  broadcast();
}

/** Subscribe to every change, including ones made in another tab. Returns an unsubscribe. */
export function onLocationChange(fn) {
  const local = () => fn(getLocation());
  const cross = (e) => { if (!e.key || e.key === CITY_KEY || e.key === LEGACY_KEY) fn(getLocation()); };
  window.addEventListener(EVENT, local);
  window.addEventListener('storage', cross);
  return () => { window.removeEventListener(EVENT, local); window.removeEventListener('storage', cross); };
}

// ── The panel's open/closed state ────────────────────────────────────────────────────────
// The pill lives in the header and the phone's copy of it lives in the FILTERS tab, which is a
// different component in a different part of the tree. They talk through here rather than
// through a context that would have to wrap the whole app.
const PANEL_EVENT = 'pr-location-panel';
let panelOpen = false;
export const isLocationPanelOpen = () => panelOpen;
export function toggleLocationPanel(next) {
  panelOpen = next == null ? !panelOpen : !!next;
  try { window.dispatchEvent(new CustomEvent(PANEL_EVENT, { detail: panelOpen })); } catch { /* no window */ }
  return panelOpen;
}
export const openLocationPanel = () => toggleLocationPanel(true);
export const closeLocationPanel = () => toggleLocationPanel(false);
export function onLocationPanel(fn) {
  const h = (e) => fn(!!e.detail);
  window.addEventListener(PANEL_EVENT, h);
  return () => window.removeEventListener(PANEL_EVENT, h);
}

/** `NOW`, `12M AGO`, `3H AGO`, `2D AGO` — the age of a recent. */
export function agoLabel(at, now = Date.now()) {
  const m = Math.round((now - at) / 60000);
  if (m < 1) return 'NOW';
  if (m < 60) return `${m}M AGO`;
  if (m < 1440) return `${Math.round(m / 60)}H AGO`;
  return `${Math.round(m / 1440)}D AGO`;
}
